/**
 * useWellnessCheckin
 *
 * Shared data-fetching hook for the Well-Being Metrics check-in graph.
 * Supports both Personal Stats (isAdmin=false) and Admin Portal (isAdmin=true).
 *
 * viewMode === "current":
 *   Fetches a daily timeseries for the current calendar month.
 *   Returns { currentSeries, currentXAxis, loading, error }.
 *
 * viewMode === "range":
 *   Fetches one-bucket monthly averages for each of prevKey + focusKey.
 *   Returns { prevSeries, focusSeries, loading, error }.
 */

import { useState, useEffect, useCallback } from "react";
import {
  getWellnessTimeseries,
  getCompanyWellnessTimeseries,
} from "../../api/checkins/checkinsApi";
import { monthKeyToRange } from "../../utils/dateUtils";

function todayIsoString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function daysInCurrentMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
}

export default function useWellnessCheckin({
  isAdmin = false,
  viewMode = "current",
  focusKey = null,
  prevKey = null,
  fetchEnabled = true,
}) {
  const fetchFn = isAdmin ? getCompanyWellnessTimeseries : getWellnessTimeseries;

  const [currentSeries, setCurrentSeries] = useState(null);
  const [currentXAxis, setCurrentXAxis] = useState([]);

  const [prevSeries, setPrevSeries] = useState(null);
  const [focusSeries, setFocusSeries] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCurrent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const buckets = 4;
      const end_date = todayIsoString();
      const data = await fetchFn({ mode: "weekly", buckets, end_date });
      setCurrentSeries(data?.series || null);
      setCurrentXAxis(
        (data?.x_axis || []).map((b) => b.label)
      );
    } catch {
      setError("Failed to load wellness data for the last 30 days.");
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  const fetchRange = useCallback(async () => {
    if (!focusKey || !prevKey) return;
    setLoading(true);
    setError(null);
    try {
      const focusRange = monthKeyToRange(focusKey);
      const prevRange = monthKeyToRange(prevKey);
      const [focusData, prevData] = await Promise.all([
        fetchFn({ mode: "monthly", buckets: 1, end_date: focusRange.end }),
        fetchFn({ mode: "monthly", buckets: 1, end_date: prevRange.end }),
      ]);
      setFocusSeries(focusData?.series || null);
      setPrevSeries(prevData?.series || null);
    } catch {
      setError("Failed to load wellness comparison data.");
    } finally {
      setLoading(false);
    }
  }, [fetchFn, focusKey, prevKey]);

  useEffect(() => {
    if (!fetchEnabled) return;
    if (viewMode === "current") {
      fetchCurrent();
    } else {
      fetchRange();
    }
  }, [fetchEnabled, viewMode, fetchCurrent, fetchRange]);

  return {
    // Current month mode
    currentSeries,
    currentXAxis,
    // Range comparison mode
    prevSeries,
    focusSeries,
    // Shared
    loading,
    error,
  };
}
